/**
 * The full-screen overlay shown while an auth request is in flight.
 *
 * Replaces a spinner sitting inside the form card, which left the rest of the
 * screen live: the fields stayed visible and apparently editable while the
 * request was already gone. This covers the whole viewport in frosted glass and
 * puts the rotating brand mark on top, so the wait is unmistakably modal.
 *
 * ONE LINE OF COPY, NO STEPS. The auth call is a single request with no
 * intermediate signal to subscribe to, so any per-stage read-out would be
 * invented — and an invented stage that asserts an outcome ("Account created")
 * becomes a lie the moment the request fails. The label names only what this
 * screen is waiting for, which stays true whatever comes back.
 */
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ScanningMark } from './ScanningMark';

export type AuthAction = 'signup' | 'signin' | 'reset' | 'password';

/** What the screen is waiting for. Present tense, no claimed outcome. */
const LABELS: Record<AuthAction, string> = {
  signup: 'Creating your account',
  signin: 'Signing you in',
  reset: 'Sending your reset link',
  password: 'Updating your password',
};

interface Props {
  action: AuthAction;
  /** Overrides the default label when the caller has something better to say. */
  label?: string;
}

export function AuthProgress({ action, label }: Props) {
  // The overlay owns the viewport while it is up, so the page behind it must not
  // scroll. Restored on unmount — including when the request fails and the form
  // comes back, which is the path that would otherwise leave the page stuck.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // PORTALLED TO document.body, and that is load-bearing rather than tidy-up.
  // The launch card that renders this form is `position: sticky`, which opens a
  // stacking context — inside it, `position: fixed` resolves against the CARD,
  // not the viewport, and the z-index is trapped below the sticky header. The
  // overlay rendered clipped to the card with the header punching through it.
  // Observed in a browser; the computed z-index read `auto`, not the 100 set here.
  return createPortal(
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 22,
        // The glass. A tint alone reads as a plain scrim, and a blur alone lets
        // busy content show through at full contrast — the two together are what
        // make the form legibly *behind* rather than merely dimmed.
        background: 'rgba(251,250,248,.72)',
        backdropFilter: 'blur(14px) saturate(1.4)',
        WebkitBackdropFilter: 'blur(14px) saturate(1.4)',
        animation: 'ibFadeIn .24s ease',
      }}
    >
      <ScanningMark size={52} />

      <p
        style={{
          margin: 0,
          fontSize: 17,
          fontWeight: 600,
          letterSpacing: '-.015em',
          color: 'var(--ink)',
          textAlign: 'center',
          padding: '0 24px',
        }}
      >
        {label ?? LABELS[action]}
        <span aria-hidden="true">…</span>
      </p>
    </div>,
    document.body,
  );
}

/**
 * The wait's lower bound, in ms.
 *
 * A sub-200ms auth response would otherwise flash the overlay for a single
 * frame — worse than no overlay, because a flash reads as a glitch. Callers
 * hold the pending state for at least this long once it has been shown.
 */
export const MIN_VISIBLE_MS = 650;
